<script setup lang="ts">
import { computed } from 'vue';
import { QLinearProgress, QBadge } from 'quasar';
import { useI18n } from 'vue-i18n';

import type { Locales, SummaryFormStatChoiceDto } from '@/services/types/api.types';
import { getTranslation } from '@/composables/data-display-utils';

// CONST
const { t } = useI18n();

// PROPS
const props = defineProps<{
  choice: SummaryFormStatChoiceDto;
  languageEnabled: Locales[];
  questionHasChoiceWithImage?: boolean;
}>();

// COMPUTED
const hasImage = computed(() => {
  return props.choice.image && props.choice.image.url;
});

const progressSize = computed(() => (props.questionHasChoiceWithImage ? '112px' : '48px'));
const progressColor = computed(() => props.choice.color);
</script>
<template>
  <div class="base-result-answer">
    <q-linear-progress
      :value="props.choice.percentage / 100"
      rounded
      :size="progressSize"
      :style="{ color: progressColor }"
    >
      <div class="base-result-answer__content">
        <div class="base-result-answer__content-left">
          <div v-if="hasImage" class="base-result-answer__content-left-image">
            <img :src="props.choice.image?.url" :alt="t('form.image-alt-text')" />
          </div>
          <div v-else-if="props.questionHasChoiceWithImage" class="base-result-answer__content-left-image-fallback">
            <q-icon name="o_photo" />
          </div>
          <q-badge
            class="text-label"
            color="transparent"
            :label="getTranslation(props.choice, 'text', props.languageEnabled)"
          />
        </div>
        <div class="base-result-answer__content-right">
          <q-badge class="value-label" color="transparent" :label="`${props.choice.percentage}%`" />
        </div>
      </div>
    </q-linear-progress>
  </div>
</template>
<style lang="scss">
.base-result-answer {
  display: flex;
  margin: $space-sm 0;

  .q-linear-progress {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    background-color: $color-neutral-grey-100 !important;
    color: $color-primary-12;
    border-radius: 6px !important;
  }

  &__content {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: inherit;
    padding: 0 $space-md;

    .q-badge {
      z-index: 1;
      font-size: $small-text !important;
      color: $color-neutral-black-text;
    }

    &-left {
      display: flex;
      flex-direction: row;
      gap: $space-md;
      align-items: center;
      justify-content: flex-start;
      width: 90%;

      .q-badge {
        display: block !important;
        max-width: 90%;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      &-image {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 12px 0;

        img {
          height: 84px;
          width: 84px;
          border-radius: 6px;
          z-index: 2;
        }

        &-fallback {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 84px;
          width: 84px;

          .q-icon {
            font-size: 84px;
            color: $color-neutral-grey-400 !important;
          }
        }
      }
    }

    &-right {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      width: 10%;
    }
  }

  @media (max-width: 1450px) {
    &__content-left {
      width: 80%;
    }

    &__content-right {
      width: 20%;
    }
  }
}
</style>
